
import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  ArrowRight,
  Plus,
  Sparkles,
  Code2,
  Zap,
  Mic,
  Rocket,
  Star,
  ArrowUp
} from "lucide-react"

const demoTexts = [
  "create a ticket portal for my party...",
  "build an event management platform...",
  "design a guest registration system...",
  "generate a community engagement app..."
]

const floatingIcons = [
  { Icon: Code2, delay: 0, duration: 3 },
  { Icon: Sparkles, delay: 0.5, duration: 4 },
  { Icon: Zap, delay: 1, duration: 3.5 },
  { Icon: Rocket, delay: 1.5, duration: 4.5 },
]

export function HeroSection() {
  const [currentText, setCurrentText] = useState("")
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTyping, setIsTyping] = useState(true)
  const [mounted, setMounted] = useState(false)
  const [inputValue, setInputValue] = useState("")

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (isTyping) {
      const fullText = demoTexts[currentIndex]
      if (currentText.length < fullText.length) {
        timeout = setTimeout(() => {
          setCurrentText(fullText.slice(0, currentText.length + 1))
        }, 50)
      } else {
        timeout = setTimeout(() => {
          setIsTyping(false)
        }, 2000)
      }
    } else {
      if (currentText.length > 0) {
        timeout = setTimeout(() => {
          setCurrentText(currentText.slice(0, -1))
        }, 30)
      } else {
        setCurrentIndex((prev) => (prev + 1) % demoTexts.length)
        setIsTyping(true)
      }
    }

    return () => clearTimeout(timeout)
  }, [currentText, currentIndex, isTyping])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputValue.trim()) {
      console.log("Generating:", inputValue)
      // Add your navigation/generation logic here
    }
  }

  if (!mounted) return null

  return (
    <section className="relative flex items-center justify-center overflow-hidden px-4 py-12 md:py-20">
      {/* Background - single gradient, no duplicate orbs */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-theme-primary/5 -z-10" />

      <div className="max-w-6xl mx-auto text-center space-y-6 md:space-y-8">
        {/* Heading - responsive sizing */}
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight">
          Built to Empower{" "}
          <span className="bg-gradient-to-r from-theme-primary to-theme-accent bg-clip-text text-transparent">
            Event Organizers
          </span>
        </h1>

        {/* Subtitle - compact */}
        <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto">
          Power events and manage guests all in one place
        </p>

        {/* Search Input - compact on mobile */}
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-theme-primary to-theme-accent rounded-2xl opacity-20 group-hover:opacity-30 blur transition" />
            <div className="relative flex items-center gap-2 bg-card/80 backdrop-blur-xl border border-border/50 rounded-xl p-3 md:p-4">
              <Plus className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={currentText}
                className="flex-1 bg-transparent border-none outline-none text-sm md:text-base"
              />
              <Button type="submit" size="sm" className="flex-shrink-0">
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground mt-3">
            Try: "Create a music festival with ticket tiers"
          </p>
        </form>

        {/* Stats - horizontal on mobile */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 pt-6">
          {[
            { value: "2.3M+", label: "Events Created" },
            { value: "99.2%", label: "Satisfaction" },
            { value: "<30s", label: "Build Time" }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-xl md:text-2xl font-bold text-foreground">{stat.value}</div>
              <div className="text-xs md:text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}